import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiPutJson, apiDelete, apiDownload, ApiError } from '../api/client';
import { formatMkd, formatDate, todayIso, monthAgoIso } from '../lib/format';
import FilterBar, { btn } from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/** Restorant — restaurant spend. Ports frmRestorant and frmRestorantAdd. */

interface RestorantRow {
  ID: number;
  date: string | null;
  shpenzimi: string | null;
  shuma: number;
}

export default function Restorant() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [form, setForm] = useState({ date: todayIso(), shpenzimi: '', shuma: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<RestorantRow | null>(null);

  const listQuery = useQuery({
    queryKey: ['restorant', search, startDate, endDate],
    queryFn: () =>
      apiGet<{ rows: RestorantRow[]; total: number }>('/restorant', {
        search,
        startDate,
        endDate,
      }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['restorant'] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/restorant/${id}`),
    onSuccess: () => {
      toast.success('Rreshti u fshi me sukses.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  function openEdit(row: RestorantRow | null) {
    setFormError(null);
    setForm(
      row
        ? {
            date: row.date?.slice(0, 10) ?? todayIso(),
            shpenzimi: row.shpenzimi ?? '',
            shuma: String(row.shuma ?? ''),
          }
        : { date: todayIso(), shpenzimi: '', shuma: '' },
    );
    setEditing({ id: row?.ID ?? null });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!form.shpenzimi.trim()) {
      setFormError('Ju lutem vendosni përshkrimin e shpenzimit.');
      return;
    }
    const amount = Number(form.shuma);
    if (form.shuma.trim() === '' || !Number.isFinite(amount)) {
      setFormError('Ju lutem vendosni një shumë të vlefshme.');
      return;
    }

    const body = { date: form.date, shpenzimi: form.shpenzimi.trim(), shuma: amount };
    try {
      if (editing?.id) await apiPutJson(`/restorant/${editing.id}`, body);
      else await apiPostJson('/restorant', body);
      setEditing(null);
      toast.success('Të dhënat u ruajtën me sukses!');
      refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Ruajtja dështoi.');
    }
  }

  const rows = listQuery.data?.rows ?? [];
  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Restorant</h1>
        <p className="text-sm text-muted">Shpenzimet e restorantit</p>
      </div>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko shpenzim"
      >
        <button
          type="button"
          onClick={() =>
            apiDownload('/restorant/eksport', { search, startDate, endDate }).catch((err) =>
              toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.'),
            )
          }
          disabled={rows.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
        <button type="button" onClick={() => openEdit(null)} className={btn.primary}>
          Shto shpenzim
        </button>
      </FilterBar>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Shpenzimi</th>
                <th className="px-4 py-3 text-right font-semibold">Shuma</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}
              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted">
                    Nuk ka shpenzime për këtë periudhë.
                  </td>
                </tr>
              )}
              {rows.map((row, index) => (
                <tr key={row.ID} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-muted">{index + 1}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.date)}</td>
                  <td className="px-4 py-2.5">{row.shpenzimi ?? '—'}</td>
                  <td className="tnum px-4 py-2.5 text-right font-medium">{formatMkd(row.shuma)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Fshij
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-muted">
            Shuma totale nga {formatDate(startDate)} deri më {formatDate(endDate)}:
          </span>
          <span className="tnum text-lg font-semibold text-ink">
            {formatMkd(listQuery.data?.total ?? 0)}
          </span>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <form
            onSubmit={handleSave}
            className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
          >
            <h2 className="mb-4 text-lg font-semibold">
              {editing.id ? 'Ndrysho shpenzimin' : 'Shto shpenzim'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rs-date">
                  Data
                </label>
                <input
                  id="rs-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rs-shpenzimi">
                  Shpenzimi
                </label>
                <input
                  id="rs-shpenzimi"
                  value={form.shpenzimi}
                  onChange={(e) => setForm({ ...form, shpenzimi: e.target.value })}
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rs-shuma">
                  Shuma
                </label>
                <input
                  id="rs-shuma"
                  inputMode="decimal"
                  value={form.shuma}
                  onChange={(e) => setForm({ ...form, shuma: e.target.value.replace(/[^\d.]/g, '') })}
                  required
                  className={`tnum ${inputClass}`}
                />
              </div>
            </div>

            {formError && (
              <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Anulo
              </button>
              <button type="submit" className={btn.primary}>
                Ruaj
              </button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message="A jeni i sigurt që dëshironi të fshini këtë rresht?"
          confirmLabel="Fshij"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.ID);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
